import { Loader } from "@common/handleErrorAndLoading/Loader";
import { Header } from "@common/header/Header";
import { ComponentType, Fragment } from "react";
import { useTranslation } from "react-i18next";
import { Redirect, Route, RouteProps } from "react-router-dom";
import { useKeycloak } from "./hook/useKeycloak";
import { keycloakUtils } from "./itsf/keycloak/keycloakUtils";
import clearStorage from "./utils/storage";

// Exact same interface as RouteProps except that component is not optional
interface IParams extends Pick<RouteProps, Exclude<keyof RouteProps, "component">> {
    component: ComponentType;
}

interface ICustomRoute extends IParams {
    isKeycloakReady: boolean;
}

const CustomRoute = ({ component: Component, isKeycloakReady, ...props }: ICustomRoute) => (
    <Route
        /* eslint-disable-next-line react/jsx-props-no-spreading */
        {...props}
        render={() => (
            <Fragment>
                {!isKeycloakReady && (
                    <div className="keycloak-loader">
                        <Loader size={70} />
                    </div>
                )}
                {isKeycloakReady && (
                    <Fragment>
                        <Header logoutCallback={clearStorage} />
                        <Component />
                    </Fragment>
                )}
            </Fragment>
        )}
    />
);

// Create route reachable only when auth
export const AuthRoute = ({ component, ...props }: IParams) => {
    const { t } = useTranslation("common");

    if (global.Keycloak) {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const { isKeycloakReady } = useKeycloak("login-required");

        /* eslint-disable-next-line react/jsx-props-no-spreading */
        return <CustomRoute {...props} component={component} isKeycloakReady={isKeycloakReady} />;
    }

    return <Error error={{ errorMessage: t("genericError") }} />;
};

// Create route reachable only when non auth
export const NonAuthRoute = ({ component, ...props }: IParams) => {
    if (global.Keycloak) {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const { isKeycloakReady } = useKeycloak("check-sso");

        return keycloakUtils.isAuthenticated() ? (
            <Redirect to="/" />
        ) : (
            /* eslint-disable-next-line react/jsx-props-no-spreading */
            <CustomRoute {...props} component={component} isKeycloakReady={isKeycloakReady} />
        );
    }

    /* eslint-disable-next-line react/jsx-props-no-spreading */
    return <CustomRoute {...props} component={component} isKeycloakReady />;
};
